import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import './ContactMessages.css';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    const q = query(
      collection(db, 'contactMessages'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching messages:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const markAsRead = async (messageId) => {
    try {
      await updateDoc(doc(db, 'contactMessages', messageId), {
        status: 'read'
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const markAsUnread = async (messageId) => {
    try {
      await updateDoc(doc(db, 'contactMessages', messageId), {
        status: 'unread'
      });
    } catch (error) {
      console.error('Error marking message as unread:', error);
    }
  };

  const deleteMessage = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await deleteDoc(doc(db, 'contactMessages', messageId));
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    return msg.status === filter;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = messages.filter(msg => msg.status === 'unread').length;

  return (
    <div className="contact-messages-page">
      <div className="page-header">
        <h1>Contact Messages</h1>
      </div>

      <div className="filter-badges">
        <button
          className={`badge-btn ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          <i className="fas fa-envelope"></i> {unreadCount} Unread
        </button>
        <button
          className={`badge-btn total ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Total: {messages.length}
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading messages...</p>
        </div>
      ) : filteredMessages.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <p>No messages found</p>
        </div>
      ) : (
        <div className="messages-list-simple">
          {filteredMessages.map(message => (
            <div key={message.id} className="message-card">
              <div className="message-card-header">
                <h3>{message.name}</h3>
                <div className="message-actions">
                  {message.status === 'unread' ? (
                    <button
                      className="action-icon-btn"
                      onClick={() => markAsRead(message.id)}
                      title="Mark as read"
                    >
                      <i className="fas fa-check-circle"></i>
                    </button>
                  ) : (
                    <button
                      className="action-icon-btn"
                      onClick={() => markAsUnread(message.id)}
                      title="Mark as unread"
                    >
                      <i className="fas fa-envelope"></i>
                    </button>
                  )}
                  <button
                    className="action-icon-btn delete"
                    onClick={() => deleteMessage(message.id)}
                    title="Delete message"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
              
              <div className="message-card-body">
                <div className="message-info-row">
                  <i className="fas fa-phone"></i>
                  <a href={`tel:${message.phone}`} className="message-link">
                    {message.phone}
                  </a>
                </div>
                <div className="message-info-row">
                  <i className="fas fa-calendar"></i>
                  <span>{formatDate(message.createdAt)}</span>
                </div>
                <div className="message-content">
                  <i className="fas fa-comment"></i>
                  <div>
                    <strong>Message:</strong>
                    <p>{message.message}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactMessages;
